// ✅ ALTERED ONLY FOR THEME (matches your “above theme”)
// - Orange header bar + white text
// - Page bg #F2F2F2 + inner white surfaces with soft blue border
// - Buttons: primary orange, outlined blue
// - Inputs: soft blue border + orange focus
// - Tables: blue header, soft zebra rows, hover tint
// - IconButtons: blue default, orange on hover
// - No API/business logic changed

import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Typography,
  TextField,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import UploadIcon from "@mui/icons-material/Upload";
import { API_URL, API_UPLOAD_URL } from "./config";

const CampusAnnouncements = () => {
  // ✅ Theme constants (same palette as your sample)
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const iconBtnSx = {
    borderRadius: 2,
    color: "rgba(25,118,210,0.95)",
    "&:hover": { backgroundColor: "rgba(240,120,40,0.10)", color: THEME_ORANGE },
  };

  const tableWrapSx = {
    ...surfaceSx,
    mt: 2,
    overflow: "hidden",
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)" },
  });

  // ===== Original state/logic =====
  const [data, setData] = useState([]);
  const [newRow, setNewRow] = useState({
    CampusCode: "",
    Title: "",
    Message: "",
  });
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [campusCode, setCampusCode] = useState("");
  const fileInputRef = useRef();

  const token = localStorage.getItem("auth");

  // Decode campusCode from JWT
  useEffect(() => {
    if (!token) return;
    try {
      const decoded = JSON.parse(atob(token.split(".")[1]));
      setCampusCode(decoded.campusCode);
      setNewRow((r) => ({ ...r, CampusCode: decoded.campusCode }));
    } catch {
      setError("Failed to decode authentication token");
    }
  }, []);

  // Central fetch function
  const fetchAnnouncements = async () => {
    if (!campusCode) return;
    try {
      const res = await axios.get(`${API_URL}/api/CampusAnnouncements`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch {
      setError("Failed to fetch announcements");
    }
  };

  // Initial load & refresh whenever campusCode changes
  useEffect(() => {
    fetchAnnouncements();
  }, [campusCode]);

  const handleInputChange = (e, id = null) => {
    const { name, value } = e.target;
    if (id === null) {
      setNewRow((r) => ({ ...r, [name]: value }));
    } else {
      setData((d) => d.map((row) => (row.id === id ? { ...row, [name]: value } : row)));
    }
  };

  const handleAddRow = async () => {
    try {
      await axios.post(`${API_URL}/api/CampusAnnouncements`, newRow, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess(true);
      setNewRow({ CampusCode: campusCode, Title: "", Message: "" });
      await fetchAnnouncements();
    } catch {
      setError("Failed to add announcement");
    }
  };

  const handleUpdateRow = async (id) => {
    try {
      const updated = data.find((row) => row.id === id);
      await axios.put(`${API_URL}/api/CampusAnnouncements/${id}`, updated, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEditId(null);
      setSuccess(true);
      await fetchAnnouncements();
    } catch {
      setError("Failed to update announcement");
    }
  };

  const handleDeleteRow = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/CampusAnnouncements/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess(true);
      await fetchAnnouncements();
    } catch {
      setError("Failed to delete announcement");
    }
  };

  const handleBulkClick = () => fileInputRef.current.click();
  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const form = new FormData();
    form.append("excelFile", file);
    form.append("CampusCode", campusCode);
    try {
      await axios.post(`${API_URL}/api/UploadCampusAnnouncements`, form, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setSuccess(true);
      await fetchAnnouncements();
    } catch {
      setError("Bulk upload failed");
    } finally {
      e.target.value = "";
    }
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Orange header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Campus Announcements
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Create, edit, delete announcements and publish updates
            </Typography>
          </Box>

          {/* Optional bulk upload (kept, you can comment out like before) */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {/* Uncomment if you want it visible */}
            {/*
            <Button variant="outlined" sx={outlinedBtnSx} startIcon={<UploadIcon /> } onClick={handleBulkClick}>
              Bulk Upload
            </Button>
            */}
            <input
              type="file"
              accept=".xlsx,.xls"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFile}
            />
          </Box>
        </Box>

        <Box sx={bodySx}>
          {/* Messages */}
          {!!error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                mb: 2,
                borderRadius: 2,
                border: "1px solid rgba(244,67,54,0.25)",
                backgroundColor: "rgba(244,67,54,0.04)",
              }}
            >
              {error}
            </Alert>
          )}

          {!!success && (
            <Snackbar open autoHideDuration={3000} onClose={() => setSuccess(false)}>
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                Operation successful!
              </Alert>
            </Snackbar>
          )}

          {/* ✅ Table */}
        {/* OUTER – keeps rounded corners & spacing */}
        <Paper elevation={0} sx={tableWrapSx}>

          {/* INNER – ONLY for horizontal scroll */}
          <Box
            sx={{
              overflowX: "auto",
              overflowY: "hidden",
            }}
          >
            <TableContainer component="div">
            <Table size="small" sx={{ width: "100%" }}>
              <colgroup>
                <col style={{ width: "3%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "20%" }} />
                <col style={{ width: "57%" }} />
                <col style={{ width: "10%" }} />
              </colgroup>

              <TableHead>
                <TableRow sx={theadRowSx}>
                  {["#", "Campus", "Title", "Message", "Actions"].map((h) => (
                    <TableCell key={h}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {data.map((row, i) =>
                  editId === row.id ? (
                    <TableRow key={row.id} sx={zebraRowSx(i)}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{row.CampusCode}</TableCell>

                      <TableCell>
                        <TextField
                          name="Title"
                          value={row.Title}
                          onChange={(e) => handleInputChange(e, row.id)}
                          size="small"
                          fullWidth
                          sx={inputSx}
                        />
                      </TableCell>

                      <TableCell>
                        <TextField
                          name="Message"
                          value={row.Message}
                          onChange={(e) => handleInputChange(e, row.id)}
                          size="small"
                          fullWidth
                          multiline
                          minRows={2}
                          sx={inputSx}
                        />
                      </TableCell>

                      <TableCell>
                        <IconButton sx={iconBtnSx} onClick={() => handleUpdateRow(row.id)} title="Save">
                          <SaveIcon />
                        </IconButton>
                        <IconButton sx={iconBtnSx} onClick={() => setEditId(null)} title="Cancel">
                          <CancelIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow key={row.id} sx={zebraRowSx(i)}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{row.CampusCode}</TableCell>
                      <TableCell>{row.Title}</TableCell>
                      <TableCell>{row.Message}</TableCell>
                      <TableCell>
                        <IconButton sx={iconBtnSx} onClick={() => setEditId(row.id)} title="Edit">
                          <EditIcon />
                        </IconButton>
                        <IconButton sx={iconBtnSx} onClick={() => handleDeleteRow(row.id)} title="Delete">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )
                )}

                {/* new-row form */}
                <TableRow sx={zebraRowSx(data.length)}>
                  <TableCell />
                  <TableCell>
                    <TextField value={campusCode} disabled fullWidth size="small" sx={inputSx} />
                  </TableCell>

                  <TableCell>
                    <TextField
                      name="Title"
                      value={newRow.Title}
                      onChange={handleInputChange}
                      placeholder="Title"
                      fullWidth
                      size="small"
                      sx={inputSx}
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      name="Message"
                      value={newRow.Message}
                      onChange={handleInputChange}
                      placeholder="Message"
                      fullWidth
                      size="small"
                      multiline
                      minRows={2}
                      sx={inputSx}
                    />
                  </TableCell>

                  <TableCell>
                    <IconButton sx={iconBtnSx} onClick={handleAddRow} title="Add">
                      <AddIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          </Box>
          </Paper>

          <Divider sx={{ mt: 2, opacity: 0 }} />
        </Box>
      </Paper>
    </Box>
  );
};

export default CampusAnnouncements;
